"use client";

import * as React from "react";
import { Plus, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { CanvasCourse } from "@/lib/types";
import { FinalAssignment } from "../../../types";
import { useExams } from "../../../hooks/useExams";

interface SuggestedStudyCarouselProps {
  assignments: FinalAssignment[];
  courses: CanvasCourse[];
  onCreateStudySet: (
    course: CanvasCourse,
    name?: string,
    exam?: FinalAssignment
  ) => void;
}

export function SuggestedStudyCarousel({
  assignments,
  courses,
  onCreateStudySet,
}: SuggestedStudyCarouselProps) {
  const { helpers } = useExams();
  // Pick a few high-signal suggestions

  const suggestions = React.useMemo(() => {
    const filteredExams = assignments.filter(
      (a) =>
        courses.some((c) => c.id.toString() === a.course_id) &&
        helpers.isExam(a)
    );
    const assignmentCards = filteredExams.slice(0, 3).map((a) => ({
      id: `assignment-${a.id}`,
      type: "assignment" as const,
      title: a.name,
      subtitle: courses.find((c) => c.id.toString() === a.course_id)?.default,
      course: courses.find((c) => c.id.toString() === a.course_id),
      dueDate: a.due_at,
      ctaLabel: "Create study set",
      assignment: a,
    }));

    const courseCards = courses.slice(0, 3).map((c) => ({
      id: `course-${c.id}`,
      type: "course" as const,
      title: c.name || c.default,
      subtitle: "Full course",
      course: c,
      ctaLabel: "Study this course",
      assignment: undefined,
    }));

    return [...assignmentCards, ...courseCards];
  }, [assignments, courses]);

  const formatDaysUntil = (date: Date) => {
    const days = Math.ceil(
      (date.getTime() - Date.now()) / (1000 * 60 * 60 * 24)
    );
    if (days === 0) return "Due today";
    if (days === 1) return "Due tomorrow";
    return `Due in ${days} days`;
  };

  if (suggestions.length === 0) return null;

  return (
    <section className="mt-10 space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold text-theme-0">Quick Start</h2>
          <p className="text-sm text-theme-1">
            Prepare for a specific exam or course
          </p>
        </div>
      </div>

      {/* Carousel */}
      <div
        className={cn(
          "flex gap-4 overflow-x-auto pb-2 pt-5",
          "snap-x snap-mandatory",
          "[-ms-overflow-style:none] [scrollbar-width:none]",
          "[&::-webkit-scrollbar]:hidden",
          "relative",

          // fade ONLY on the right edge
          "[mask-image:linear-gradient(to_right,black_0%,black_90%,transparent_100%)]",
          "[-webkit-mask-image:linear-gradient(to_right,black_0%,black_90%,transparent_100%)]"
        )}
      >
        {suggestions.map((item) => (
          <div
            key={item.id}
            className="relative min-w-[280px] max-w-[320px] snap-start"
          >
            {/* Due date wrapper (outside the card) */}
            {item.type === "assignment" && item.dueDate && (
              <div className="absolute -top-3 right-0 z-10">
                <div className="rounded-full bg-theme-0 px-2.5 py-1 text-xs font-semibold text-theme-0 shadow-theme">
                  {formatDaysUntil(new Date(item.dueDate))}
                </div>
              </div>
            )}

            {/* Card */}
            <div
              className={cn(
                "relative rounded-xl",
                "transition-transform cursor-pointer group"
              )}
              onClick={() =>
                onCreateStudySet(
                  item.course,
                  `${item.title} Study Set`,
                  item.assignment
                )
              }
            >
              {/* Course color wash */}
              <div
                className="absolute inset-0 rounded-xl opacity-25 group-hover:opacity-50 transition"
                style={{
                  backgroundColor: item.course.color,
                  backgroundImage: item.course.img
                    ? `url(${item.course.img})`
                    : "none",
                }}
              />

              <div className="relative flex h-full flex-col justify-between p-5">
                <div className="space-y-1">
                  <div className="flex items-center space-x-2">
                    <div
                      className="h-3 w-3 rounded-full shrink-0"
                      style={{
                        backgroundColor: item.course?.color || "#ffffff80",
                      }}
                    />
                    <p className="text-xs font-medium text-theme-1 line-clamp-1">
                      {item.subtitle}
                    </p>
                  </div>

                  <h3 className="line-clamp-2 text-base font-semibold text-theme-0">
                    {item.title}
                  </h3>
                </div>

                <Button
                  size="sm"
                  className="mt-4 w-fit gap-1 shadow-none bg-white/50 group-hover:bg-white/30 font-bold"
                >
                  <Plus className="h-4 w-4" />
                  {item.ctaLabel}
                </Button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}
